import React, { useMemo } from 'react';
import { FieldValues, FormState, useFormContext } from 'react-hook-form';
import Box from '@material-ui/core/Box';

import { InputField } from 'shared/forms';
import { useTranslation } from 'hooks/useTranslation';
import useValidation from 'hooks/useValidation';

import { SelectInputField } from 'features/app/components/SelectInputField';
import BaseButton from 'features/app/components/BaseButton';

import { useStyles } from './styles';

import currencies from 'features/finance/constants/currencies.json';
import { PaymentTypesOptions } from 'features/finance/constants/payment-types-options';

type Props = {
  onNextStep: () => void;
};

const isAmountPartValid = (formState: FormState<FieldValues>, amount?: string, currencyCode?: string) =>
  Object.keys(formState.errors).length > 0 || !amount || !currencyCode;

export const TopUpBalanceAmountPart: React.FC<Props> = ({ onNextStep }) => {
  const classes = useStyles();
  const { t } = useTranslation('finance');
  const { required, minNumber, moneyValue } = useValidation();

  const { watch, formState } = useFormContext() ?? {};

  const amount = watch('amount');
  const currency = watch('currency');

  const isNextButtonDisabled = useMemo(
    () => isAmountPartValid(formState, amount, currency?.code),
    [amount, currency, formState]
  );

  const handleNextStepBtnClick = () => {
    if (isNextButtonDisabled) return;

    onNextStep();
  };

  return (
    <>
      <SelectInputField
        name="currency"
        label={t('topUpBalanceForm.currency')}
        getOptionLabel={o => `${o.code} (${o.name})`}
        options={currencies}
        rules={{
          validate: {
            ...required,
          },
        }}
        className={classes.topUpInputField}
      />
      <InputField
        name="amount"
        fullWidth
        placeholder={t('inputs.enter', { ns: 'common' })}
        label={t('topUpBalanceForm.amount')}
        required
        type="number"
        rules={{
          validate: {
            ...required,
            ...minNumber(1),
            amount: moneyValue,
          },
        }}
        className={classes.topUpInputField}
      />

      <SelectInputField
        name="payment_method"
        label={t('topUpBalanceForm.paymentMethod')}
        getOptionLabel={o => o.name}
        options={PaymentTypesOptions}
        isDisabled
        isClearable={false}
        rules={{
          validate: {
            ...required,
          },
        }}
        className={classes.topUpInputField}
      />
      <Box display="flex" justifyContent="center" paddingTop={4.5}>
        <Box minWidth="219px" display="flex">
          <BaseButton
            disabled={isNextButtonDisabled}
            onClick={handleNextStepBtnClick}
            className={classes.nextButton}
            labelClass={classes.nextButtonLabel}
          >
            {t('next', { ns: 'common' })}
          </BaseButton>
        </Box>
      </Box>
    </>
  );
};